import { useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PersonId } from '@shared/brand';
import { CalendarDate } from '@domain/value-objects/CalendarDate';
import { DateRange } from '@domain/value-objects/DateRange';
import { subAreaColor } from '@domain/entities/Organization';
import type { VacationRequest } from '@domain/entities/VacationRequest';
import { OCCUPYING_STATUSES } from '@domain/vocabulary';
import { suggestWindows } from '@domain/policies/ReschedulePolicy';
import type { OccupiedSpan } from '@domain/policies/OverlapPolicy';
import {
  prepareVacationRequest,
  type BalanceAfter,
  type VacationRequestAssessment,
} from '@application/use-cases/PrepareVacationRequest';
import { getServices } from '@infrastructure/services';
import { Avatar, FilterChip, cssVars, cx } from '../../design-system';
import { formatBusinessDays, formatDateRange, formatDay, formatDayWithYear } from '../../format';
import { useAccess } from '../../hooks/useAccess';
import { matchesCandidate } from './candidateSearch';
import { awayDuring } from './replacementAvailability';
import { serverAnswer } from '../../serverAnswer';
import { useVacationEligibility } from '../../hooks/useVacationEligibility';
import { useRuntimeConfigState } from '../../providers/RuntimeConfigProvider';
import { useSession } from '../../providers/SessionProvider';
import { useToast } from '../../providers/ToastProvider';
import { WizardShell, wizardStyles as w } from '../../wizard/WizardShell';
import { RangePicker } from '../../wizard/RangePicker';
import picker from '../../wizard/RangePicker.module.css';
import styles from './RequestWizard.module.css';

/**
 * Nueva solicitud — the four-step flow.
 *
 * The screen holds the draft; every judgement about it comes from
 * `prepareVacationRequest`. That split is deliberate: whether ten days are
 * affordable, whether the sub area is at its cap, whether the notice is short
 * and how many signatures the request needs are all rules the server re-checks
 * on submit, so computing them here would be a second implementation that could
 * drift from the first.
 *
 * Warnings do not block. The cap and the notice period are judgements a leader
 * makes, not laws — the design surfaces them and lets the person send anyway.
 * The one hard stop is a balance that cannot cover the request.
 */

interface WorkItem {
  readonly id: string;
  readonly ticket: string;
  readonly text: string;
}

const STEPS = [
  { label: 'Fechas' },
  { label: 'Entrega' },
  { label: 'Reemplazo' },
  { label: 'Confirmación' },
] as const;

/*
 * How far past the original a suggestion starts looking.
 *
 * A week. The range was refused because something was in the way, and that
 * something rarely moves by tomorrow — offering «un día después» reads as not
 * having understood the refusal.
 */
const RESCHEDULE_LEAD_DAYS = 7;

/**
 * The ceiling on every free-text box in the wizard.
 *
 * Five hundred characters is a paragraph — enough to hand over a project and
 * short enough that nobody pastes a document into a field a leader has to read
 * on a card. The server enforces the same number; this is what stops somebody
 * reaching it by surprise at the end of a four-step form.
 */
const TEXT_LIMIT = 500;

/** Lo que el servidor acepta como número de ticket. */
const TICKET_LIMIT = 40;


/**
 * The saved draft.
 *
 * It used to live in `sessionStorage`, which empties when the tab closes — so
 * «guardar borrador» promised something it could not keep, and «la próxima vez»
 * was already too late. `localStorage` survives the tab; the key carries the
 * person id so a shared machine never hands one colleague's half-written
 * request to the next.
 */
interface StoredDraft {
  readonly step: number;
  readonly startISO: string | null;
  readonly endISO: string | null;
  readonly notes: string;
  readonly savedAt: string;
}

const draftKey = (personId: string): string => `vacaciones:draft:${personId}`;

/**
 * How long a draft is worth restoring.
 *
 * Past this it is not a draft, it is an artefact: the dates it holds have very
 * likely gone by, and reopening the wizard on a range in the past would put
 * somebody in front of a form the server refuses.
 */
const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const readDraft = (personId: string): StoredDraft | null => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = window.localStorage.getItem(draftKey(personId));
    if (raw === null) return null;

    const parsed = JSON.parse(raw) as Partial<StoredDraft>;
    const savedAt = typeof parsed.savedAt === 'string' ? Date.parse(parsed.savedAt) : Number.NaN;

    if (!Number.isFinite(savedAt) || Date.now() - savedAt > DRAFT_MAX_AGE_MS) {
      window.localStorage.removeItem(draftKey(personId));
      return null;
    }

    return {
      step: typeof parsed.step === 'number' ? parsed.step : 0,
      startISO: typeof parsed.startISO === 'string' ? parsed.startISO : null,
      endISO: typeof parsed.endISO === 'string' ? parsed.endISO : null,
      notes: typeof parsed.notes === 'string' ? parsed.notes : '',
      savedAt: parsed.savedAt ?? '',
    };
  } catch {
    // A corrupt draft is not worth a crash on open; it is worth forgetting.
    return null;
  }
};


export const RequestWizard = ({
  onClose,
  onSubmitted,
  rescheduleFrom = null,
}: {
  onClose: () => void;
  onSubmitted: () => Promise<void>;
  /**
   * The request being replaced, handed over whole by the screen that opened
   * this — not looked up here.
   *
   * Fetching it by folio is a query the security rules refuse: a `folio` filter
   * proves neither `inScope` nor `isSelf`, so Firestore will not run it. That
   * failed silently in production — no suggestions, no error — while demo mode,
   * which evaluates no rules, showed it working. The caller already holds the
   * object.
   */
  rescheduleFrom?: VacationRequest | null;
}): ReactNode => {
  const rescheduleFromFolio = rescheduleFrom?.folio ?? null;
  const session = useSession();
  const toast = useToast();
  const { featureOn } = useAccess();

  /*
   * Read once, at mount, and actually applied below — the step, the range and
   * the notes. Before, the draft was written and never read back: the banner
   * said «retomamos el borrador que guardaste» over a blank form.
   */
  const [restoredDraft] = useState(() => readDraft(session.personId));

  const [step, setStep] = useState(() => restoredDraft?.step ?? 0);
  const [range, setRange] = useState<DateRange | null>(() => {
    if (restoredDraft?.startISO === undefined || restoredDraft.startISO === null) return null;
    if (restoredDraft.endISO === null) return null;

    const start = CalendarDate.fromISO(restoredDraft.startISO);
    const end = CalendarDate.fromISO(restoredDraft.endISO);
    if (!start.ok || !end.ok) return null;

    const created = DateRange.create(start.value, end.value);
    return created.ok ? created.value : null;
  });
  const [work, setWork] = useState<readonly WorkItem[]>([
    { id: 'w1', ticket: '', text: '' },
  ]);
  const [replacement, setReplacement] = useState<PersonId | null>(null);
  /*
   * Whose sub area to draw a replacement from.
   *
   * Own sub area by default: the person who covers you is normally the person
   * who does your work, and on a thirty-seven person area the whole list is
   * noise around the four names that matter.
   */
  const [scope, setScope] = useState<'sub' | 'todos'>('sub');
  const [candidateSearch, setCandidateSearch] = useState('');

  const [notes, setNotes] = useState(() => restoredDraft?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [draftFound, setDraftFound] = useState(() => restoredDraft !== null);
  const [independentReschedule, setIndependentReschedule] = useState(false);

  const today = getServices().clock.today();
  const eligibility = useVacationEligibility();
  /**
   * `loaded` is false until the runtime permission matrix has been read at least
   * once; `error` is set when it could not be read at all and the session is
   * running on factory defaults.
   */
  const { config: liveConfig, loaded: configLoaded, error: configError } = useRuntimeConfigState();

  const holidays = useQuery({
    queryKey: ['wizard', 'holidays', today.year],
    queryFn: async () => {
      const result = await getServices().holidays.nonWorkingDates([today.year, today.year + 1]);
      return result.ok ? result.value : new Set<string>();
    },
  });

  /** Who from the sub area is already away, so the grid can dot those days. */
  const subAreaAbsences = useQuery({
    queryKey: ['wizard', 'subAreaAbsences', session.subAreaId],
    enabled: session.subAreaId !== null,
    queryFn: async () => {
      if (session.subAreaId === null) return [];
      const result = await getServices().requests.list(
        // The area travels with the sub area or the rules refuse the query.
        { areaId: session.areaId, subAreaId: session.subAreaId, statuses: OCCUPYING_STATUSES },
        { limit: 100 },
      );
      return result.ok
        ? result.value.items.filter((r) => r.personId !== session.personId)
        : [];
    },
  });

  /**
   * Quién del área entera estará fuera en las fechas elegidas.
   *
   * Aparte de `subAreaAbsences` porque responde otra pregunta: aquélla pinta el
   * calendario del equipo; ésta dice a quién no se puede nombrar respaldo, y el
   * respaldo se puede elegir de cualquier sub área. Ver `awayDuring`.
   */
  const areaAbsences = useQuery({
    queryKey: ['wizard', 'areaAbsences', session.areaId, range?.start.toISO(), range?.end.toISO()],
    enabled: range !== null,
    queryFn: async () => {
      if (range === null) return [];
      const result = await getServices().requests.list(
        { areaId: session.areaId, statuses: OCCUPYING_STATUSES, touching: range },
        { limit: 200 },
      );
      return result.ok ? result.value.items : [];
    },
  });

  const awayColleagues = useMemo(
    () => (range === null ? new Map<PersonId, string>() : awayDuring(areaAbsences.data ?? [], range)),
    [areaAbsences.data, range],
  );

  /*
   * El respaldo elegido deja de contar si las fechas cambian y ya no está: quien
   * vuelve al paso de fechas después de elegirlo no debe enviarlo sin verlo.
   */
  const chosenReplacement =
    replacement !== null && !awayColleagues.has(replacement) ? replacement : null;

  const busyDays = useMemo(() => {
    const map = new Map<string, string>();
    for (const absence of subAreaAbsences.data ?? []) {
      for (const day of absence.range.toDayArray()) {
        map.set(day.toISO(), `${absence.personName} está fuera`);
      }
    }
    return map;
  }, [subAreaAbsences.data]);

  const colleagues = useQuery({
    queryKey: ['wizard', 'colleagues', session.areaId, session.personId],
    queryFn: async () => {
      const result = await getServices().people.list(
        { areaId: session.areaId, status: 'active' },
        { limit: 200 },
      );
      return result.ok
        ? result.value.items.filter((p) => p.id !== session.personId)
        : [];
    },
  });

  const subAreas = useQuery({
    queryKey: ['wizard', 'subAreas', session.areaId],
    queryFn: async () => {
      const result = await getServices().organization.listSubAreas(session.areaId);
      return result.ok ? result.value : [];
    },
  });

  /**
   * The assessment. Re-run whenever the range changes, and it is the single
   * source for the day count, the cap, the balance and the approval route.
   *
   * `personId` is in the key on purpose. The answer is entirely person-specific
   * — their balance, their leaders, their sub area's cap — so keying it on the
   * range alone lets one person's verdict be served to the next, which is how
   * a cached «no tienes permiso» from an administrator ends up in front of an
   * employee who does.
   */
  const assessment = useQuery({
    queryKey: [
      'wizard',
      'assessment',
      session.personId,
      range?.toString() ?? '',
      rescheduleFromFolio,
      // The matrix decides the first thing this query answers, so a change to it
      // has to invalidate the verdict rather than leave a stale «no» on screen
      // for the minute `staleTime` keeps it.
      liveConfig.rolePermissions[session.role].join(','),
    ],
    /*
     * Not until the live configuration has landed, and that is the whole of the
     * second failure reported from the floor.
     *
     * `requireAccess` reads the permission matrix out of the session, and until
     * the listener delivers its first snapshot that matrix is the factory
     * default — where `admin` and `super` deliberately hold no `solicitar`,
     * because a superuser configures the company rather than filing requests.
     * The live matrix in this company grants it to all four roles, so for those
     * two roles the wizard answered «No tienes permiso para solicitar
     * vacaciones» against a configuration that says the opposite, for however
     * long the first snapshot took. Provisional data must not produce a verdict.
     *
     * `loaded` also turns true on the listener's error path, so a configuration
     * that genuinely cannot be read still resolves to the defaults rather than
     * spinning — with the provider's own banner explaining it.
     */
    enabled: range !== null && configLoaded,
    queryFn: async () => {
      if (range === null) return null;
      const services = getServices();
      const result = await prepareVacationRequest(
        {
          clock: services.clock,
          requests: services.requests,
          holidays: services.holidays,
          leaders: services.leaders,
          balances: services.balances,
          people: services.people,
          organization: services.organization,
        },
        /*
         * The live configuration, not the copy the session is carrying.
         *
         * `Session` holds a snapshot of the config and a separate effect keeps it
         * in step, which is one copy more than this decision needs — and the two
         * disagreeing is precisely how a superuser was told they may not request
         * vacation while «Roles y permisos» showed the permission granted, right
         * there on the same screen. Reading the provider's value here removes the
         * possibility rather than tightening the synchronisation.
         */
        { ...session, config: liveConfig },
        {
          range,
          ...(rescheduleFromFolio === null ? {} : { replacesRequestFolio: rescheduleFromFolio }),
        },
      );
      /*
       * The failure is carried, not swallowed.
       *
       * Returning null on error made the step render an empty panel with no
       * explanation and no way forward — the reader cannot tell a range that is
       * still loading from one the server refused to assess.
       */
      return result;
    },
  });

  const check = assessment.data?.ok === true ? assessment.data.value : null;
  /*
   * A denial has two very different causes and they must not read the same.
   *
   * `requireAccess` consults the permission matrix in the session. When the
   * company's live matrix could not be read, that matrix is the factory default
   * — which withholds `solicitar` from `admin` and `super` on purpose — and the
   * verdict then describes a configuration nobody chose. Saying «no tienes
   * permiso» there sends a superuser to audit their own role, which is exactly
   * where the answer is not: the fault is upstream, in a configuration read that
   * failed, and `configError` is the provider already knowing it.
   */
  const checkError =
    assessment.error !== null
      ? `No pudimos evaluar esas fechas: ${assessment.error.message}`
      : assessment.data?.ok === false
        ? assessment.data.error.kind === 'access-denied'
          ? configError !== null
            ? `El portal está usando los permisos de fábrica porque no pudo leer la configuración de la empresa, y en ésos tu rol (${session.role}) no solicita vacaciones. ${configError}`
            : 'No tienes permiso para solicitar vacaciones.'
          : 'No pudimos evaluar esas fechas. Revisa tu conexión e intenta de nuevo.'
        : null;
  const subAreaById = new Map((subAreas.data ?? []).map((s) => [s.id, s]));

  /** Colleagues from the viewer's own sub area — the default and usual answer. */
  const ownSubAreaCandidates = useMemo(
    () => (colleagues.data ?? []).filter((person) => person.subAreaId === session.subAreaId),
    [colleagues.data, session.subAreaId],
  );

  /**
   * What the picker actually renders.
   *
   * Own sub area first even in the wider scope: widening the search should add
   * names below the likely ones, not shuffle them away.
   */
  const visibleCandidates = useMemo(() => {
    const base = scope === 'sub' ? ownSubAreaCandidates : (colleagues.data ?? []);
    const matched = base.filter((person) => {
      const sub = person.subAreaId === null ? undefined : subAreaById.get(person.subAreaId);
      return matchesCandidate(
        { displayName: person.displayName, subAreaName: sub?.name ?? '' },
        candidateSearch,
      );
    });

    /* Quien estará fuera va al final: sigue a la vista, para que nadie lo busque
       creyendo que falta, pero no entre los primeros que se ofrecen. */
    const rank = (person: (typeof matched)[number]): number =>
      (awayColleagues.has(person.id) ? 2 : 0) + (person.subAreaId === session.subAreaId ? 0 : 1);

    return [...matched].sort((a, b) => rank(a) - rank(b));
    // `subAreaById` is rebuilt each render; the query data behind it is the real
    // dependency and is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    scope,
    candidateSearch,
    ownSubAreaCandidates,
    colleagues.data,
    subAreas.data,
    session.subAreaId,
    awayColleagues,
  ]);

  /**
   * Where else those days would fit.
   *
   * `suggestWindows` already existed and was only ever called when a leader
   * *rejected* a request — the alternatives were computed, counted in the audit
   * line and then dropped, so the person who needed them never saw one. Running
   * it here is what turns «Reagendar» from an empty calendar into an answer.
   *
   * The search starts a week after the original rather than tomorrow. A range
   * that was refused for colliding with somebody is unlikely to stop colliding
   * the next morning, and the first offer being «un día después» reads as the
   * portal not having understood the problem. Notice still wins if it is longer:
   * a suggestion nobody could legally book is worse than no suggestion.
   */
  const suggestions = useMemo(() => {
    const from = rescheduleFrom;
    if (from === null) return [];

    const peers: OccupiedSpan[] = (subAreaAbsences.data ?? [])
      .filter((absence) => absence.folio !== from.folio)
      .map((absence) => ({
        personId: absence.personId,
        personName: absence.personName,
        range: absence.range,
        tentative: absence.status !== 'Aprobada' && absence.status !== 'Tomada',
      }));

    const sub = session.subAreaId === null ? undefined : subAreaById.get(session.subAreaId);
    const noticeFloor = today.addDays(session.config.rules.noticeDays);
    const weekAfter = from.range.start.addDays(RESCHEDULE_LEAD_DAYS);

    return suggestWindows({
      businessDays: from.businessDays,
      searchFrom: CalendarDate.max(noticeFloor, weekAfter),
      existing: peers,
      cap: sub?.maxConcurrentOut ?? session.config.rules.defaultMaxConcurrentOut,
      nonWorking: holidays.data ?? new Set<string>(),
      limit: 3,
    });
    // `subAreaById` is rebuilt each render; the query data behind it is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rescheduleFrom, subAreaAbsences.data, subAreas.data, holidays.data, session, today]);

  /** Days already claimed by someone on the same sub area, within the range. */
  const conflicts = useMemo(() => {
    if (range === null) return [];
    return (subAreaAbsences.data ?? []).filter((absence) => absence.range.overlaps(range));
  }, [range, subAreaAbsences.data]);

  const requiresReplacement = featureOn('reemplazo');

  const stepValid = ((): boolean => {
    switch (step) {
      case 0:
        /*
         * El traslape consigo mismo detiene el paso, ya no sólo lo comenta.
         *
         * Era una advertencia que dejaba seguir, y en producción acabaron tres
         * personas con dos ausencias el mismo día —una de ellas con un día
         * aprobado como vacaciones y validado como incapacidad a la vez, o sea
         * un día de saldo gastado por un día que estuvo enferma—. El servidor lo
         * rechaza desde ahora; esto es para que nadie llegue hasta el final del
         * asistente para enterarse.
         */
        return (
          range !== null &&
          /*
           * Y que no haya empezado ya.
           *
           * `submitVacationRequest` rechaza todo rango que arranque hoy o antes
           * —«esas fechas ya empezaron»— y el calendario ya no deja elegirlo. Esto
           * cubre el rango que llega hecho desde otro sitio: reagendar una
           * solicitud vieja lo siembra con sus fechas originales, y entonces el
           * aviso previo salía en **negativo** —«estás avisando con -22 días»—,
           * que no es un aviso sino un imposible con cara de advertencia.
           */
          !range.start.isBefore(eligibility.earliestBookable) &&
          check !== null &&
          check.balance.affordable &&
          (check.selfOverlap === null || check.selfOverlap === undefined)
        );
      /*
       * El paso 1 —la entrega— ya no detiene nada. Lo que lo detenía era el
       * registro firmado en PDF, obligatorio con la bandera `pdfObl`: el portal
       * es ahora ese registro, y la bandera, el paso de subirlo y sus avisos se
       * quitaron el 7 oct 2026.
       */
      case 2:
        return !requiresReplacement || chosenReplacement !== null;
      default:
        return true;
    }
  })();

  const submit = async (): Promise<void> => {
    if (range === null) return;
    setBusy(true);

    const result = await getServices().requests.submit({
      range,
      replacementPersonId: chosenReplacement,
      notes,
      workItems: work
        .filter((item) => item.ticket.trim().length > 0 || item.text.trim().length > 0)
        .map((item) => ({ ticket: item.ticket.trim(), text: item.text.trim() })),
      rescheduledFromFolio: rescheduleFromFolio,
    });
    setBusy(false);

    if (!result.ok) {
      toast.bad(
        result.error.kind === 'permission-denied'
          ? 'No tienes permiso para enviar solicitudes.'
          : (serverAnswer(result.error, 'submitVacationRequest') ??
              'No pudimos enviar la solicitud. Revisa tu conexión.'),
      );
      return;
    }

    toast.ok(
      `${result.value.folio} enviada. ${
        result.value.requiredSignatures === 2
          ? 'Necesita dos firmas.'
          : 'Tu líder ya la tiene en su bandeja.'
      }`,
    );
    await onSubmitted();
  };

  const next = (): void => {
    if (step < STEPS.length - 1) {
      setStep((current) => current + 1);
      return;
    }
    void submit();
  };

  const saveDraftAndClose = (): void => {
    if (typeof window !== 'undefined') {
      const draft: StoredDraft = {
        step,
        // The dates as ISO, not `toString()`: a formatted range is for reading,
        // and what comes back has to be parseable.
        startISO: range?.start.toISO() ?? null,
        endISO: range?.end.toISO() ?? null,
        notes,
        savedAt: new Date().toISOString(),
      };
      window.localStorage.setItem(draftKey(session.personId), JSON.stringify(draft));
    }
    toast.info('Guardamos tu borrador para retomarlo después.');
    onClose();
  };

  const discardAndClose = (): void => {
    if (typeof window !== 'undefined') window.localStorage.removeItem(draftKey(session.personId));
    setDraftFound(false);
    onClose();
  };

  return (
    <>
      <WizardShell
      eyebrow="Flujo de solicitud"
      title="Nueva solicitud de vacaciones"
      steps={STEPS}
      current={step}
      nextLabel={step === STEPS.length - 1 ? 'Enviar solicitud' : 'Continuar'}
      nextDisabled={!stepValid}
      busy={busy}
      onBack={() => {
        setStep((current) => Math.max(0, current - 1));
      }}
      onNext={next}
      onClose={() => {
        setConfirmClose(true);
      }}
      banner={
        <>
          {rescheduleFromFolio !== null && !independentReschedule && (
            <div className={styles.rescheduleBand}>
              <span className={styles.rescheduleGlyph} aria-hidden="true">
                ↺
              </span>
              <div className={styles.rescheduleText}>
                <div>Estás reagendando {rescheduleFromFolio}. Elige fechas nuevas; el folio anterior queda enlazado para que se lea la historia completa.</div>
                <div className={styles.suggestionLabel}>Fechas sugeridas · respetan el tope de tu sub área</div>
                <button
                  type="button"
                  className={styles.independentButton}
                  onClick={() => {
                    setIndependentReschedule(true);
                  }}
                >
                  Hacerla independiente
                </button>
              </div>
            </div>
          )}
          {draftFound && (
            <div className={styles.draftBand}>
              <span className={styles.draftGlyph} aria-hidden="true">↺</span>
              <span>Retomamos el borrador que guardaste.</span>
              <button
                type="button"
                onClick={() => {
                  if (typeof window !== 'undefined') {
                    window.localStorage.removeItem(draftKey(session.personId));
                  }
                  setDraftFound(false);
                  setStep(0);
                  setRange(null);
                  setNotes('');
                }}
              >
                Empezar de cero
              </button>
            </div>
          )}
        </>
      }
      aside={<WizardAside conflicts={conflicts} range={range} check={check} />}
    >
      {step === 0 && (
        <div className={w.stepBody}>
          <div>
            <h3 className={w.stepTitle}>¿Cuándo te vas?</h3>
            <p className={w.stepNote}>
              Toca el primer día y luego el último — o arrastra con el mouse. Con teclado: Tab
              para llegar al calendario, flechas para moverte y Enter para marcar. Solo se
              cuentan días hábiles.
            </p>

            <RangePicker
              value={range}
              onChange={setRange}
              /*
               * The anniversary, not today, for someone still in their first
               * year: the days exist on paper from day one and become takeable
               * at twelve months, so the calendar opens where they can actually
               * start. `submitVacationRequest` refuses an earlier range anyway —
               * this is what stops them picking one.
               */
              minimum={eligibility.earliestBookable}
              holidays={holidays.data ?? new Set()}
              busy={busyDays}
            />

            {/* Offered above the shortcuts, because for somebody who was just
                refused these are the answer and «Una semana» is a blank slate. */}
            {suggestions.length > 0 && (
              <div className={styles.suggestions}>
                <div className={styles.suggestionsTitle}>
                  Ventanas libres para tus{' '}
                  {formatBusinessDays(rescheduleFrom?.businessDays ?? 0)}
                </div>
                <div className={styles.suggestionRow}>
                  {suggestions.map((suggestion) => (
                    <button
                      key={suggestion.range.toString()}
                      type="button"
                      className={cx(
                        styles.suggestion,
                        range?.toString() === suggestion.range.toString() &&
                          styles.suggestionActive,
                      )}
                      onClick={() => {
                        setRange(suggestion.range);
                      }}
                    >
                      <span className={styles.suggestionDates}>
                        {formatDateRange(suggestion.range)}
                      </span>
                      <span className={styles.suggestionNote}>
                        {suggestion.peakPeopleOut === 0
                          ? 'Nadie más fuera'
                          : `${String(suggestion.peakPeopleOut)} fuera ese tramo`}
                        {suggestion.holidaysInside.length > 0 &&
                          ` · ${String(suggestion.holidaysInside.length)} feriado${
                            suggestion.holidaysInside.length === 1 ? '' : 's'
                          }`}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className={picker.quick}>
              {[
                /*
                 * «Dos semanas» primero: es lo que el portal recomienda en el
                 * resto del flujo, y el primer atajo de una fila es el que la
                 * gente pulsa. Ofrecer «Una semana» de entrada contradecía el
                 * consejo de la propia pantalla.
                 */
                { label: 'Dos semanas', days: 13 },
                { label: 'Una semana', days: 6 },
                { label: 'Un día', days: 0 },
              ].map((option) => (
                <button
                  key={option.label}
                  type="button"
                  className={picker.quickButton}
                  onClick={() => {
                    /*
                     * Un atajo respeta el aviso previo; el calendario no.
                     *
                     * Son dos cosas distintas y por eso el suelo es distinto. El
                     * aviso previo —quince días en esta empresa— **no bloquea**:
                     * el servidor acepta fechas más cercanas y el asistente sólo
                     * advierte, porque a veces hay que pedir con dos días y eso lo
                     * decide un líder, no una regla. Así que el calendario sigue
                     * dejando elegir desde mañana.
                     *
                     * Pero un atajo es una **sugerencia** del portal, y sugerir un
                     * rango que dispara su propia advertencia es proponerle a
                     * alguien que empiece con un aviso amarillo encima. Arrancan,
                     * pues, en el primer día que no lo provoca.
                     *
                     * `max` de los dos y no sólo el aviso: quien todavía no cumple
                     * el año tiene su aniversario más lejos, y ese suelo manda.
                     */
                    const start = CalendarDate.max(
                      eligibility.earliestBookable,
                      today.addDays(session.config.rules.noticeDays),
                    );
                    const created = DateRange.create(start, start.addDays(option.days));
                    if (created.ok) setRange(created.value);
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {checkError !== null && <div className={cx(w.warn, w.blocker)}>{checkError}</div>}

          {/*
            * Cero días hábiles no es falta de saldo, y decirlo así confundía.
            *
            * Pedir el 16 de septiembre —feriado— daba «no te alcanza el saldo:
            * pediste 0 días hábiles y tienes 13», que manda a acortar un rango
            * cuando lo que hay que hacer es alargarlo o moverlo. El saldo no tiene
            * nada que ver: no hay nada que descontar.
            */}
          {check !== null && !check.balance.affordable && check.days.businessDays === 0 && (
            <div className={cx(w.warn, w.blocker)}>
              Esas fechas no tienen ningún día hábil: caen en fin de semana o en feriado, así
              que no habría nada que descontarte. Elige otras o alarga el rango.
            </div>
          )}

          {check !== null && !check.balance.affordable && check.days.businessDays > 0 && (
            <div className={cx(w.warn, w.blocker)}>
              No te alcanza el saldo: pediste {check.days.businessDays} días hábiles y en esas
              fechas te alcanzan{' '}
              {check.balance.error?.kind === 'insufficient'
                ? check.balance.error.available
                : check.balance.available}
              . Acorta el rango para poder enviarla.
            </div>
          )}

          {/*
            * De dónde salen, cuando salen de más de un periodo.
            *
            * Con «Vencimiento del saldo» los periodos se enciman y pagan primero los
            * días que vencen antes; unas mismas fechas pueden terminar lo que queda
            * del periodo viejo y seguir con el nuevo. Con uno solo no hay nada que
            * explicar y no se dice nada.
            */}
          {check !== null && check.balance.affordable && check.balance.shares.length > 1 && (
            <div className={w.warn}>
              Primero se gastan los días que vencen antes:{' '}
              {check.balance.shares
                .map(
                  (share) =>
                    share.projected
                      ? `${formatBusinessDays(share.days)} de tu periodo ${String(share.year)}, que abre el ${formatDayWithYear(CalendarDate.fromISOOrThrow(share.startsOn))}`
                      : `${formatBusinessDays(share.days)} de tu periodo ${String(share.year)} (vigentes hasta el ${formatDayWithYear(CalendarDate.fromISOOrThrow(share.lastValidDay))})`,
                )
                .join(' y ')}
              .
            </div>
          )}

          {/*
            * Un solo periodo paga, pero no es el de las fechas.
            *
            * Pedir abril con aniversario en marzo gasta lo que queda del periodo
            * anterior, que sigue vigente, y no el que acaba de abrir. Sin decirlo,
            * «saldo después» se lee como si fuera del periodo nuevo.
            */}
          {check !== null &&
            check.balance.affordable &&
            check.balance.shares.length === 1 &&
            check.balance.shares[0] !== undefined &&
            check.balance.laterPeriod !== null && (
              <div className={w.warn}>
                Estos {formatBusinessDays(check.balance.shares[0].days)} salen de tu periodo{' '}
                {check.balance.shares[0].year}, vigente hasta el{' '}
                {formatDayWithYear(CalendarDate.fromISOOrThrow(check.balance.shares[0].lastValidDay))},
                y no de tu periodo {check.balance.laterPeriod.year}, que abre el{' '}
                {formatDayWithYear(CalendarDate.fromISOOrThrow(check.balance.laterPeriod.startsOn))}:
                primero se gastan los días que vencen antes.
              </div>
            )}

          {/*
            * Lo que se pierde, dicho antes de enviar.
            *
            * Días de un periodo que vence antes de las fechas elegidas: esta
            * solicitud no los puede gastar, y quien los tiene suele creer que sí.
            */}
          {check !== null && check.balance.expiringUnused !== null && (
            <div className={w.warn}>
              Tienes {formatBusinessDays(check.balance.expiringUnused.days)} de tu periodo{' '}
              {check.balance.expiringUnused.year} que vencen el{' '}
              {formatDayWithYear(CalendarDate.fromISOOrThrow(check.balance.expiringUnused.lastValidDay))},
              antes de estas fechas, así que no se usan aquí. Si quieres gozarlos, elige fechas
              hasta ese día.
            </div>
          )}

          {/*
            Un rango que ya empezó se dice como lo que es: un bloqueo.
            Antes caía en el aviso de abajo y salía con los días en negativo, que
            se lee como una política rara y no como «esto no se puede pedir».
          */}
          {range !== null && range.start.isBefore(eligibility.earliestBookable) && (
            <div className={cx(w.warn, w.blocker)}>
              Esas fechas ya empezaron. Las vacaciones se piden por adelantado: elige desde el{' '}
              {formatDay(eligibility.earliestBookable)}.
            </div>
          )}

          {check !== null &&
            check.balance.affordable &&
            !check.notice.sufficient &&
            range !== null &&
            !range.start.isBefore(eligibility.earliestBookable) && (
              <div className={w.warn}>
                Estás avisando con {check.notice.daysGiven} días y la política pide{' '}
                {check.notice.required}. Puedes enviarla igual; tu líder verá el aviso corto.
              </div>
            )}

          {check?.overlap !== null && check?.overlap?.exceedsCap === true && (
            <div className={w.warn}>
              En esas fechas tu sub área llegaría a {check.overlap.peakPeopleOut} personas fuera y el tope es{' '}
              {check.overlap.cap}. No lo bloqueamos, pero es lo primero que va a mirar tu líder.
            </div>
          )}

          {check?.selfOverlap !== null && check?.selfOverlap !== undefined && (
            <div className={cx(w.warn, w.blocker)}>
              Ya tienes una ausencia registrada del{' '}
              {formatDateRange(check.selfOverlap.range)}. No puedes estar fuera dos veces a la vez.
            </div>
          )}
        </div>
      )}

      {step === 1 && (
        <div className={w.stepBody}>
          <div>
            <h3 className={w.stepTitle}>¿En qué estuviste trabajando?</h3>
            <p className={w.stepNote}>Para que tu reemplazo sepa dónde quedó cada cosa.</p>

            <div className={styles.workList}>
              {work.map((item) => (
                <div key={item.id} className={styles.workRow}>
                  <input
                    type="text"
                    className={styles.workTicket}
                    placeholder="IOS-482"
                    value={item.ticket}
                    // El servidor acota el folio de ticket a cuarenta; más que eso
                    // no es un identificador.
                    maxLength={TICKET_LIMIT}
                    onChange={(event) => {
                      const ticket = event.target.value.slice(0, TICKET_LIMIT);
                      setWork((current) =>
                        current.map((w2) => (w2.id === item.id ? { ...w2, ticket } : w2)),
                      );
                    }}
                  />
                  <input
                    type="text"
                    className={styles.workText}
                    placeholder="Estado actual y qué falta"
                    value={item.text}
                    maxLength={TEXT_LIMIT}
                    onChange={(event) => {
                      const text = event.target.value.slice(0, TEXT_LIMIT);
                      setWork((current) =>
                        current.map((w2) => (w2.id === item.id ? { ...w2, text } : w2)),
                      );
                    }}
                  />
                  <button
                    type="button"
                    className={styles.workRemove}
                    aria-label="Quitar frente de trabajo"
                    onClick={() => {
                      setWork((current) => current.filter((w2) => w2.id !== item.id));
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            <button
              type="button"
              className={styles.addWork}
              onClick={() => {
                setWork((current) => [
                  ...current,
                  { id: `w${String(current.length + 1)}-${String(current.length)}`, ticket: '', text: '' },
                ]);
              }}
            >
              + Agregar frente de trabajo
            </button>
          </div>

        </div>
      )}

      {step === 2 && (
        <div className={w.stepBody}>
          <div>
            <h3 className={w.stepTitle}>¿Quién te reemplaza?</h3>
            <p className={w.stepNote}>
              Primero los de tu sub área. Quien tenga fechas cruzadas queda bloqueado.
            </p>

            <div className={styles.scopeRow}>
              <FilterChip
                active={scope === 'sub'}
                count={ownSubAreaCandidates.length}
                color={subAreaColor(
                  session.subAreaId === null ? undefined : subAreaById.get(session.subAreaId),
                )}
                onClick={() => {
                  setScope('sub');
                  setCandidateSearch('');
                }}
              >
                Mi sub área
              </FilterChip>
              <FilterChip
                active={scope === 'todos'}
                count={(colleagues.data ?? []).length}
                onClick={() => {
                  setScope('todos');
                }}
              >
                Todos
              </FilterChip>
            </div>

            {/* Only with the wider scope: four names need no search box. */}
            {scope === 'todos' && (
              <input
                type="search"
                className={styles.search}
                placeholder="Buscar por nombre o sub área…"
                aria-label="Buscar a quién te reemplaza"
                value={candidateSearch}
                onChange={(event) => {
                  setCandidateSearch(event.target.value);
                }}
                onKeyDown={(event) => {
                  /*
                   * Escape clears the field; it must not close the wizard.
                   *
                   * `WizardShell` listens for Escape on the document, which is
                   * right for a modal — and a search input uses the same key to
                   * empty itself. Without this, somebody who typed a name and
                   * pressed Escape to start over would lose four steps of form
                   * instead of four letters.
                   */
                  if (event.key === 'Escape' && candidateSearch.length > 0) {
                    event.stopPropagation();
                    setCandidateSearch('');
                  }
                }}
              />
            )}

            {visibleCandidates.length === 0 ? (
              <div className={styles.candidatesEmpty}>
                {candidateSearch.trim().length > 0
                  ? `Nadie coincide con «${candidateSearch.trim()}».`
                  : 'No hay nadie más en tu sub área. Cambia a «Todos».'}
              </div>
            ) : (
            <div
              className={cx(
                styles.candidates,
                scope === 'todos' && styles.candidatesScrollable,
              )}
            >
              {visibleCandidates
                .map((person) => {
                  const awayNote = awayColleagues.get(person.id);
                  const away = awayNote !== undefined;
                  const sa =
                    person.subAreaId === null ? undefined : subAreaById.get(person.subAreaId);
                  const picked = chosenReplacement === person.id;

                  return (
                    <button
                      key={person.id}
                      type="button"
                      disabled={away}
                      className={cx(styles.candidate, picked && styles.candidatePicked)}
                      onClick={() => {
                        setReplacement(picked ? null : person.id);
                      }}
                    >
                      <Avatar name={person.displayName} color={subAreaColor(sa)} size={38} />
                      <span className={styles.candidateText}>
                        <span className={styles.candidateName}>{person.displayName}</span>
                        <span className={styles.candidateNote}>
                          {sa?.name ?? 'Sin sub área'} ·{' '}
                          {awayNote ?? 'disponible'}
                        </span>
                      </span>
                      <span className={styles.candidateMark} aria-hidden="true">
                        {picked ? '✓' : away ? '×' : ''}
                      </span>
                    </button>
                  );
                })}
            </div>
            )}
          </div>

          <label className={w.field}>
            <span className={w.fieldLabel}>Notas para el reemplazo (opcional)</span>
            <textarea
              className={w.textarea}
              placeholder="Accesos, reuniones recurrentes, contactos clave…"
              value={notes}
              maxLength={TEXT_LIMIT}
              onChange={(event) => {
                // Cortado además del `maxLength`: el atributo detiene el tecleo y
                // no siempre un pegado, y lo que importa es lo que se envía.
                setNotes(event.target.value.slice(0, TEXT_LIMIT));
              }}
            />
            <span className={styles.charCount}>
              {String(notes.length)} / {String(TEXT_LIMIT)}
            </span>
          </label>
        </div>
      )}

      {step === 3 && (
        <div className={w.stepBody}>
          <div>
            <h3 className={w.stepTitle}>Todo listo, revisa el resumen</h3>

            <div className={w.summary}>
              <SummaryRow k="Periodo" v={range === null ? '—' : formatDateRange(range)} />
              <SummaryRow
                k="Días hábiles"
                v={check === null ? '—' : String(check.days.businessDays)}
              />
              <SummaryRow
                k="Saldo después"
                v={
                  /* Un cero y un «no lo sé» no son lo mismo. Un periodo que
                     acaba de abrir puede no tener documento de saldo todavía
                     —lo crea el servidor—, y decir «0 días» ahí se lee como
                     «no tienes vacaciones». */
                  check === null || !check.balance.known
                    ? '—'
                    : describeSaldoDespues(check.balance.after)
                }
              />
              <SummaryRow
                k="Reemplazo"
                v={
                  chosenReplacement === null
                    ? 'Sin designar'
                    : ((colleagues.data ?? []).find((p) => p.id === chosenReplacement)?.displayName ??
                      '—')
                }
              />
              <SummaryRow
                k="Frentes de trabajo"
                v={
                  work.filter((i) => i.text.trim().length > 0).length === 0
                    ? 'Ninguno anotado'
                    : work
                        .filter((i) => i.text.trim().length > 0)
                        .map((i) => (i.ticket.length > 0 ? `${i.ticket} · ${i.text}` : i.text))
                        .join(' — ')
                }
              />
            </div>

            {check !== null && check.requiredSignatures === 2 && (
              <div className={styles.routeCard}>
                <div className={styles.routeLabel}>Ruta de aprobación · dos firmas</div>
                <div className={styles.routeSteps}>
                  {check.approverNames.map((name, index) => (
                    <div key={name} className={styles.routeStep}>
                      <span className={styles.routeNumber}>{index + 1}</span>
                      <span className={styles.routeWho}>{name}</span>
                      <span className={styles.routeRole}>
                        {index === 0 ? 'aprueba' : 'da el visto bueno'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <p className={styles.routeNote}>
              {check === null
                ? ''
                : check.requiredSignatures === 2
                  ? 'Trabajas con más de un líder, así que la solicitud necesita las dos firmas antes de quedar aprobada.'
                  : /* Sin líder asignado la recibe quien administra el área —así la
                       encamina `submitVacationRequest`—, y decir «tu líder» sería
                       nombrar a alguien que no existe. */
                    `${
                      check.approverNames[0] ?? 'Quien administra tu área'
                    } recibirá la solicitud en cuanto la envíes.`}
            </p>
          </div>
        </div>
      )}
      </WizardShell>
      {confirmClose && (
        <div className={styles.confirmOverlay} role="dialog" aria-modal="true">
          <div className={styles.confirmCard}>
            <h3>¿Salir de la solicitud?</h3>
            <p>Tu información se perderá si sales sin guardar un borrador.</p>
            <div className={styles.confirmActions}>
              <button type="button" className={styles.saveDraftButton} onClick={saveDraftAndClose}>
                Guardar borrador y salir
              </button>
              <button
                type="button"
                className={styles.keepEditingButton}
                onClick={() => {
                  setConfirmClose(false);
                }}
              >
                Seguir editando
              </button>
              <button type="button" className={styles.discardButton} onClick={discardAndClose}>
                Descartar todo
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const SummaryRow = ({ k, v }: { k: string; v: string }): ReactNode => (
  <div className={w.summaryRow}>
    <div className={w.summaryKey}>{k}</div>
    <div className={w.summaryValue}>{v}</div>
  </div>
);

/** The context panel: who else from the sub area is out on the chosen dates. */
/**
 * The right-hand column: what the chosen range costs, and who else is out.
 *
 * The three figures used to sit under the calendar and appear the moment a range
 * was assessed, which pushed «Continuar» down the page — the button moved out
 * from under the cursor that had just finished choosing dates, and the person had
 * to scroll to find it again. Here they cost no vertical space in the column
 * being read, and they fill a panel that was empty until a range was picked.
 */
const WizardAside = ({
  conflicts,
  range,
  check,
}: {
  conflicts: readonly { id: string; personName: string; range: DateRange }[];
  range: DateRange | null;
  check: VacationRequestAssessment | null;
}): ReactNode => (
  <div className={styles.aside}>
    {check !== null && (
      <div className={styles.asideStats}>
        <div
          className={picker.stat}
          style={cssVars({
            '--stat-bg': 'var(--color-brand-bg)',
            '--stat-fg': 'var(--color-brand-ink)',
          })}
        >
          <div className={picker.statLabel}>Días hábiles</div>
          <div className={picker.statValue}>{check.days.businessDays}</div>
        </div>
        <div
          className={picker.stat}
          style={cssVars({
            '--stat-bg': check.balance.affordable
              ? 'var(--color-success-bg)'
              : 'var(--color-danger-bg)',
            '--stat-fg': check.balance.affordable
              ? 'var(--color-success-ink)'
              : 'var(--color-danger-ink)',
          })}
        >
          <div className={picker.statLabel}>Saldo después</div>
          {/*
            * Una línea por periodo que conserva días, del que vence antes al que
            * vence después.
            *
            * Con dos periodos vivos una sola cifra mezcla días que vencen en fechas
            * distintas, y eso es justo lo que la persona necesita separar: «te
            * quedan 23» no dice que uno se pierde en septiembre. Un periodo que se
            * agota no se lista; si no queda nada en ninguno, se dice cero.
            */}
          {!check.balance.known ? (
            <div className={picker.statValue}>—</div>
          ) : (
            <SaldoDespues after={check.balance.after} />
          )}
        </div>
        <div className={picker.stat}>
          <div className={picker.statLabel}>Regresas el</div>
          <div className={picker.statValue}>
            {formatDay(CalendarDate.fromISOOrThrow(check.returnsOn))}
          </div>
        </div>
      </div>
    )}

    <div className={w.asideLabel}>Tu sub área en esas fechas</div>

    {range === null ? (
      <p className={styles.asideEmpty}>Elige un rango y te decimos quién más estará fuera.</p>
    ) : conflicts.length === 0 ? (
      <p className={styles.asideEmpty}>
        Nadie más de tu sub área tiene ausencias en esas fechas. Camino libre.
      </p>
    ) : (
      <ul className={styles.asideList}>
        {conflicts.map((absence) => (
          <li key={absence.id} className={styles.asideRow}>
            <Avatar name={absence.personName} color="var(--color-brand)" size={32} />
            <span className={styles.asideText}>
              <span className={styles.asideName}>{absence.personName}</span>
              <span className={styles.asideRange}>{formatDateRange(absence.range)}</span>
            </span>
          </li>
        ))}
      </ul>
    )}

  </div>
);

/** Los periodos que conservan días después de la solicitud; ninguno si todo se agota. */
const conDias = (after: readonly BalanceAfter[]): readonly BalanceAfter[] =>
  after.filter((periodo) => periodo.days > 0);

/**
 * Cómo se dice el periodo de una línea: cuándo vence lo que ya corre, cuándo abre
 * lo que todavía no empieza.
 */
const cuandoDe = (periodo: BalanceAfter): string =>
  periodo.projected
    ? `abre el ${formatDayWithYear(CalendarDate.fromISOOrThrow(periodo.startsOn))}`
    : `hasta el ${formatDayWithYear(CalendarDate.fromISOOrThrow(periodo.lastValidDay))}`;

/** «17 días del periodo 2027» o «1 día del periodo 2026 y 22 del 2027». */
const describeSaldoDespues = (after: readonly BalanceAfter[]): string => {
  const quedan = conDias(after);
  if (quedan.length === 0) return '0 días';
  return quedan
    .map(
      (periodo, indice) =>
        `${String(periodo.days)} ${periodo.days === 1 ? 'día' : 'días'} ${
          indice === 0 ? 'del periodo' : 'del'
        } ${String(periodo.year)}`,
    )
    .join(' y ');
};

const SaldoDespues = ({ after }: { after: readonly BalanceAfter[] }): ReactNode => {
  const quedan = conDias(after);
  if (quedan.length === 0) return <div className={picker.statValue}>0</div>;
  return (
    <div className={styles.saldoDespues}>
      {quedan.map((periodo) => (
        <div key={periodo.year} className={styles.saldoPeriodo}>
          <span className={picker.statValue}>{periodo.days}</span>
          <span className={styles.saldoPeriodoNota}>
            del {periodo.year} · {cuandoDe(periodo)}
          </span>
        </div>
      ))}
    </div>
  );
};
